import {
  SUPPLY_LOSS_FORECAST_DAYS,
  SUPPLY_LOSS_WINDOW_DAYS,
  SUPPLY_STOCKOUT_FORECAST_DAYS,
  type SupplyEventKind,
  type SupplyStockoutCause,
} from '@gozoki/api-types'
import { formatNumber } from '../../lib/utils'

export const PARIS_ZONE = 'Europe/Paris'

export const SUPPLY_COLORS = {
  // Design system token utility-error-400, carried by the breakage icon (Figma node 8741:31915).
  breakage: '#f87171',
  forecast: '#526d4a',
  previousForecast: '#4f7cac',
  sales: '#efa031',
  stock: '#96b389',
  stockoutMark: '#f0a6b4',
  // Darker pair for the row buttons, where the chart tints would vanish on the green table.
  breakageText: '#dc2626',
  stockoutText: '#c94b6d',
  bandGradientFrom: '#e5e1d3',
  bandGradientTo: '#efe9d3',
  cream: '#f9f8f1',
  pageBg: '#f0ede2',
  darkText: '#152013',
  errorText: '#dc2626',
  panelText: '#072c1a',
  tableBg: '#cee3c4',
  tableSelectedRow: 'rgba(82, 109, 74, 0.35)',
} as const

// Legend swatches from Figma node 8741:33658: they match how the series finally look
// (stock is drawn at 70% opacity), not their raw fill colour.
export const SUPPLY_LEGEND_COLORS = {
  stocks: '#b3c8ad',
  defectiveStocks: '#ad8282',
  sales: '#efa031',
  forecastSales: '#526d4a',
  correctedSales: '#f8d4a2',
  delivery: '#5e7d4f',
  deliveryMark: '#6a9560',
  orders: '#ad8282',
} as const

export const SUPPLY_EVENT_LABELS: Record<SupplyEventKind, string> = {
  breakage: 'Casse',
  delivery: 'Livraison',
  production_order: 'Ordre de production',
  stockout: 'Rupture',
}

export const STOCKOUT_CAUSE_LABELS: Record<SupplyStockoutCause, string> = {
  defective_stock: 'Stock défectueux',
  depleted: 'Stock épuisé',
}

export const SUPPLY_KPI_LABELS = {
  correctedSales: 'Vente cor.',
  expectedLanding: 'Att. vente',
  expectedStock: 'Att. stock',
  forecastElapsed: 'VPH cum.',
  sales: 'Vente',
  stock: 'Stock',
  vpj: 'VPJ',
  vph: 'VPH',
} as const

export const SUPPLY_PANEL_TITLES = {
  sales: 'Ventes',
  breakage: 'Casse',
  stockout: 'Rupture',
} as const

export const FORECAST_DISCLAIMER = 'Estimation provisoire'

export const CUMULATED_FORECAST_TOOLTIP = `Ventes attendues depuis minuit jusqu'à l'heure actuelle, à comparer aux ventes réelles. ${FORECAST_DISCLAIMER}.`

export const EXPECTED_STOCK_TOOLTIP = `Stock attendu ce soir : stock actuel, plus la livraison du jour, moins les ventes prévues d'ici minuit. ${FORECAST_DISCLAIMER}.`

export const SUPPLY_LEGEND_DESCRIPTIONS = {
  stocks: "Articles présents en boutique à l'heure affichée, paniers en cours inclus.",
  defectiveStocks: 'Articles encore en boutique mais retirés de la vente.',
  sales: "Unités effectivement vendues pendant l'heure.",
  forecastSales: `Ventes attendues sur l'heure, d'après la prévision calculée chaque nuit par produit et boutique. ${FORECAST_DISCLAIMER}.`,
  correctedSales:
    'Ventes réelles augmentées de ce que la rupture a empêché de vendre : la prévision de chaque heure passée à stock zéro, de la détection au retour en stock, produit par produit.',
  delivery: "Réception d'une commande fournisseur.",
  orders: 'Ordre de production envoyé au fabricant.',
  previousForecast:
    'Ce que le moteur prévoyait pour la même heure il y a 24 heures : la dernière version écrite au moins 24 h avant maintenant, en bleu.',
} as const

export const LOSS_FORECAST_LABEL = `Prév. ${SUPPLY_LOSS_FORECAST_DAYS} j`
export const LOSS_WINDOW_LABEL = `${SUPPLY_LOSS_WINDOW_DAYS} jours`

const WINDOWS_INDEPENDENCE_TOOLTIP =
  'Les deux sont indépendants de la période sélectionnée ci-dessus.'

const WINDOWS_TOOLTIP = `${LOSS_FORECAST_LABEL} : ce que le stock en rayon, ses DLC, les livraisons connues et les ventes prévues annoncent pour les ${SUPPLY_LOSS_FORECAST_DAYS} prochains jours, aujourd'hui inclus. Les livraisons ne sont connues que jusqu'à demain ; au-delà, on suppose qu'elles couvrent la prévision du jour. ${LOSS_WINDOW_LABEL} : ce qui a été constaté sur les ${SUPPLY_LOSS_WINDOW_DAYS} derniers jours pleins, jusqu'à hier, comme le tableau de bord réseau. ${WINDOWS_INDEPENDENCE_TOOLTIP}`

export const SALES_WINDOW_TOOLTIP = `Unités vendues. Sur la ligne ${LOSS_WINDOW_LABEL}, le nombre compte les ventes des ${SUPPLY_LOSS_WINDOW_DAYS} derniers jours pleins, jusqu'à hier, et le taux les compare à ce que le moteur prévoyait le matin même pour ces journées. Sur la ligne ${LOSS_FORECAST_LABEL}, le nombre additionne les ventes déjà faites aujourd'hui et la prévision jusqu'à la fin des ${SUPPLY_LOSS_FORECAST_DAYS} prochains jours, et le taux donne l'évolution attendue face aux ventes de ces ${SUPPLY_LOSS_WINDOW_DAYS} derniers jours. ${FORECAST_DISCLAIMER}. ${WINDOWS_INDEPENDENCE_TOOLTIP}`

export const BREAKAGE_RATE_TOOLTIP = `Articles retirés à l'inventaire après leur DLC, comptés sur la veille du retrait comme le tableau de bord réseau. Sur la ligne ${LOSS_WINDOW_LABEL}, le taux rapporte leur valeur d'achat au chiffre d'affaires HT ; sur la ligne ${LOSS_FORECAST_LABEL}, il rapporte les unités qui devraient périmer aux unités prévues à la vente. Le nombre à gauche compte les unités. ${WINDOWS_TOOLTIP}`

export const STOCKOUT_TOOLTIP = `Part de la demande non servie faute de produit en rayon. Sur la ligne ${LOSS_WINDOW_LABEL} : ventes que la rupture a fait perdre sur les ${SUPPLY_LOSS_WINDOW_DAYS} derniers jours pleins, jusqu'à hier, estimées d'après la prévision de la veille sur les heures sans stock et corrigées de la tendance de chaque boutique, rapportées à la demande totale des produits concernés. Sur la ligne ${LOSS_FORECAST_LABEL} : même calcul de demain 9 h, première livraison qu'une commande peut encore changer, jusqu'à la fin de J+${SUPPLY_STOCKOUT_FORECAST_DAYS} (J étant aujourd'hui), en supposant que chaque livraison est commandée d'après la prévision disponible au moment de la commande (ventes prévues des jours couverts × coefficient de sécurité − stock), et non d'après les besoins de réappro réellement envoyés. Le nombre à gauche compte les unités perdues. ${WINDOWS_INDEPENDENCE_TOOLTIP}`

// Front and back can be briefly out of step on a deploy: an absent field must not print "NaN".
export const formatInt = (value: number | null | undefined): string =>
  typeof value === 'number' && Number.isFinite(value) ? formatNumber(value) : '-'

export const formatPercent = (value: number): string =>
  `${value.toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`

export const formatSignedPercent = (value: number): string =>
  `${value > 0 ? '+' : ''}${formatPercent(value)}`

// A version can cover fewer hours than the timeline shows, so a missing slot reads as no value.
export const previousForecastValue = (
  sales: (number | null)[] | undefined,
  index: number,
): number | null => {
  const value = sales?.[index]
  return typeof value === 'number' && Number.isFinite(value) ? Math.round(value) : null
}
